import { intersection } from 'lodash'
import { balanceErrorThreshold, Provider } from './providers/common/Provider'
import {
  checkForInvalid,
  createMapToRemainingDemand,
  createSecondaryRequirement,
  EnergyFlow,
  SecondaryEnergyFlow,
  EnergyType,
  PrimaryEnergyFlow,
  Quadrant,
  isSecondaryEnergyFlow,
  isRequirement,
} from './EnergyFlow'
import { reduceToSumOfItems } from '../util/array-utility'
import { sortProvidersByPriority } from './providers/util/provider-utility'
import { isSolarPanelProvider } from './providers/SolarPanelProvider'
import { isBatteryProvider } from './providers/BatteryProvider'

export abstract class SystemContainerCalculator {
  private readonly providers: Provider[] = []

  private readonly demands: PrimaryEnergyFlow[] = []

  private hasProvided = false

  public constructor(public readonly id: string) {}

  public addProvider(provider: Provider): void {
    this.providers.push(provider)
  }

  public addDemand(demand: PrimaryEnergyFlow): void {
    this.demands.push(demand)
  }

  public calculateUsage(): void {
    this.provideForDemand([EnergyType.Heating, EnergyType.Cooling])
    this.provideForDemand([EnergyType.Electricity])

    this.hasProvided = true
  }

  public checkBalance(): void {
    if (!this.hasProvided) {
      throw new Error('Please calculate usage before checking balance.')
    }

    this.providers.forEach((provider) => {
      provider.checkBalance()
    })

    this.checkTotalBalance()
  }

  public getPrimaryDemands(): PrimaryEnergyFlow[] {
    return this.demands
  }

  public getProviders(): Provider[] {
    return this.providers.sort(sortProvidersByPriority)
  }

  public getProviderEnergyFlows(): EnergyFlow[] {
    return this.providers
      .flatMap((provider) => provider.flows)
      .filter(checkForInvalid)
  }

  public getHourlyTotalsBy(quadrants: Quadrant[]): number[] {
    const mapEnergyFlowToHourSeriesWithCoolingNegated = (
      flow: EnergyFlow,
    ): number[] =>
      flow.energyType === EnergyType.Cooling
        ? flow.hourSeries.map((value) => -value)
        : flow.hourSeries

    return this.getProviderEnergyFlows()
      .filter((flow) => quadrants.some((q) => q === flow.quadrant))
      .map(mapEnergyFlowToHourSeriesWithCoolingNegated)
      .reduce(reduceToSumOfItems, [])
  }

  private checkForInvalidProvider(
    provider: Provider,
  ): provider is Provider & { flows: EnergyFlow[] } {
    if (!provider.flows) {
      throw new Error('Please check calculation order')
    }

    return true
  }

  private mapProviderFlowsToRequiredElectricity(
    provider: Provider & { flows: EnergyFlow[] },
  ): EnergyFlow[] {
    return provider.flows
      .filter((flow) => flow.quadrant === Quadrant.Requirement)
      .filter((flow) => flow.energyType === EnergyType.Electricity)
      .map((flow) =>
        createSecondaryRequirement(
          provider.id,
          flow.energyType,
          flow.hourSeries,
        ),
      )
  }

  private getRequiredElectricityFromProviders(): EnergyFlow[] {
    return this.providers
      .filter(
        (provider) =>
          provider.providedEnergyTypes.includes(EnergyType.Heating) ||
          provider.providedEnergyTypes.includes(EnergyType.Cooling),
      )
      .filter(this.checkForInvalidProvider)
      .flatMap(this.mapProviderFlowsToRequiredElectricity)
  }

  private getEnergyDemands(energyTypes: EnergyType[]): EnergyFlow[] {
    const buildingDemands = this.demands.filter((flow) =>
      energyTypes.includes(flow.energyType),
    )

    if (energyTypes.includes(EnergyType.Electricity)) {
      return [...buildingDemands, ...this.getRequiredElectricityFromProviders()]
    }

    return buildingDemands
  }

  private provideForDemand(energyTypes: EnergyType[]): void {
    const demands = this.getEnergyDemands(energyTypes)
    let remainingDemands = [...demands]
    const overproductions: SecondaryEnergyFlow[] = []

    this.providers
      .filter(
        (provider) =>
          intersection(energyTypes, provider.providedEnergyTypes).length > 0,
      )
      .sort(sortProvidersByPriority)
      .forEach((provider) => {
        const demandsForEnergyType = remainingDemands.filter((flow) =>
          provider.providedEnergyTypes.includes(flow.energyType),
        )
        const filteredDemands = provider.providesElectricityForProviders
          ? demandsForEnergyType
          : demandsForEnergyType.filter(
              (demand) => demand.quadrant !== Quadrant.Requirement,
            )

        const allEnergyFlows = provider.provideForDemand(
          filteredDemands,
          overproductions,
        )

        const providedEnergyFlows = allEnergyFlows.filter(
          (flow) => flow.quadrant === Quadrant.Provided,
        )

        remainingDemands = remainingDemands.map(
          createMapToRemainingDemand(providedEnergyFlows),
        )

        const wasteEnergyFlows = allEnergyFlows
          .filter(isSecondaryEnergyFlow)
          .filter((flow) => flow.quadrant === Quadrant.Waste)

        overproductions.push(...wasteEnergyFlows)
      })

    const requirementFlows = this.providers
      .filter(isBatteryProvider)
      .flatMap((batteryProvider) =>
        batteryProvider.flows
          ? batteryProvider.flows
              .filter(isSecondaryEnergyFlow)
              .filter(isRequirement)
          : [],
      )

    this.fixSolarPanelEnergyFlows(requirementFlows)
  }

  private checkTotalBalance(): void {
    const incomingSeries = this.getHourlyTotalsBy([
      Quadrant.Requirement,
      Quadrant.FromEnvironment,
    ])
    const outgoingSeries = this.getHourlyTotalsBy([
      Quadrant.Waste,
      Quadrant.Provided,
    ])

    if (incomingSeries.length !== outgoingSeries.length) {
      throw new Error('Data length mismatch when checking building balance')
    }

    incomingSeries.forEach((incoming, hour) => {
      if (Math.abs(incoming - outgoingSeries[hour]) > balanceErrorThreshold) {
        throw new Error(
          `Error in building balance: in ${incoming}, out ${outgoingSeries[hour]} for hour ${hour}`,
        )
      }
    })
  }

  private fixSolarPanelEnergyFlows(
    batteryRequirementFlows: SecondaryEnergyFlow[],
  ): void {
    const solarPanelProviders = this.providers.filter(isSolarPanelProvider)

    batteryRequirementFlows.forEach((requirementFlow) => {
      const providerToUpdate = solarPanelProviders.find(
        (provider) => provider.id === requirementFlow.providerId,
      )
      if (providerToUpdate) {
        providerToUpdate.fixOverproduction(requirementFlow)
      }
    })
  }
}
